import React from 'react';
import { Button, Card, Row, Col, Typography, Dropdown, Menu, Modal, Spin, Empty, Input, Tag, Table } from 'antd';
import { PlusOutlined, MoreOutlined, EditOutlined, DeleteOutlined, FileExcelOutlined, FilePptOutlined, DownloadOutlined } from '@ant-design/icons';
import groupServices from '../../services/groupServices';
import assetGroupService from '../../services/assetGroupService';
import organizationService from '../../services/organizationService';
import authStore from '../../store/authStore';
import constants from '../../config/constants';
import toast from '../../components/toast';
import Pagination from 'react-js-pagination';
import CreateGroupModal from '../../components/creategroupmodal/CreateGroupModal';
import Confirmmodal from '../../components/confirmmodal';
import Utility from '../../helpers/Utility';
import Permissions from '../../config/permssions';
import CreateAssetGroupModal from './CreateAssetGroupModal';
import EditAssetGroupModal from './EditAssetGroupModal';
import monthlyReportService, { IAssetGroupReportHistoryItem } from '../../services/monthlyReportService';
import GenerateMonthlyReportModal from '../../components/monthly-report/GenerateMonthlyReportModal';

const { Title, Text } = Typography;

const {
  view_assigned_users_sensorboxes_assets,
  create_or_update_sensorboxes_assets,
  delete_sensorboxes_assets,
  view_only_partial_asset_sensorbox_details,
} = Permissions as any;

type AssetItem = {
  id: string;
  name: string;
  sensorBoxCount: number;
};

type BoxItem = {
  id: string;
  name: string;
  place: string;
};

type AssetSection = {
  key: string;
  title: string;
  description: string;
  totalAssetsText: string;
  totalBoxesText: string;
  collapsed?: boolean;
  assets: AssetItem[];
  sensorboxes: BoxItem[];
};

type AssetAction = 'edit' | 'delete';
type AssetGroupAction = 'edit' | 'delete' | 'generate-report' | 'report-history';

function AssetCard({ item, onAction, index, payload }: { item: AssetItem; onAction: (action: AssetAction, payload: any) => void; index: number; payload?: any }) {
  const menu = (
    <Menu
      selectable={false}
      onClick={({ key }) => onAction(key as AssetAction, payload ?? item)}
      items={[
        { key: 'edit', icon: <EditOutlined />, label: 'Edit' },
        { type: 'divider' as const },
        { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
      ]}
    />
  );

  return (
    <Card
      className="asset-card"
      style={{ borderRadius: 10 ,  cursor:'pointer'}}
      bodyStyle={{ padding: 16, position: 'relative' }}
    >
      <Row align="middle" justify="space-between">
        <Col>
          <Title level={5} style={{ margin: 0 }}>{item.name}</Title>
          <Text type="secondary">{item.sensorBoxCount} sensor boxes</Text>
        </Col>
        <Col>
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Col>
      </Row>
    </Card>
  );
}

function AssetGroupsAssetCard({ item }: { item: AssetItem}) {
  return (
    <Card
      className="asset-card"
      style={{ borderRadius: 10 ,  cursor:'pointer'}}
      bodyStyle={{ padding: 16, position: 'relative' }}
    >
      <Row align="middle" justify="space-between">
        <Col>
          <Title level={5} style={{ margin: 0 }}>{item.name}</Title>
          <Text type="secondary">{item.sensorBoxCount} sensor boxes</Text>
        </Col>
      </Row>
    </Card>
  );
}

function BoxCard({ item }: { item: BoxItem }) {

  return (
    <Card
      className="asset-card"
      style={{ borderRadius: 10 ,  cursor:'pointer'}}
      bodyStyle={{ padding: 16, position: 'relative' }}
    >
      <Row align="middle" justify="space-between">
        <Col>
          <Title level={5} style={{ margin: 0 }}>{item.name}</Title>
          <Text type="secondary">{item.place}</Text>
        </Col>
      </Row>
    </Card>
  );
}

const AssetGroups: React.FC = () => {
  const [groupList, setGroupList] = React.useState<Array<any>>([]);
  const [assetGroupList, setAssetGroupList] = React.useState<Array<any>>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [assetGroupLoading, setAssetGroupLoading] = React.useState<boolean>(false);
  const [selectedOrgId, setSelectedOrgId] = React.useState<string | undefined>(undefined);
  const [count, setCount] = React.useState<number>(0);
  const [assetGroupCount, setAssetGroupCount] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(1);
  const [assetGroupPage, setAssetGroupPage] = React.useState<number>(1);
  const [sectionsPage, setSectionsPage] = React.useState<number>(1);
  const [modalVisible, setModalVisible] = React.useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = React.useState<any>(null);
  const [createModal, setCreateModal] = React.useState<boolean>(false);
  const [editAssetGroupModal, setEditAssetGroupModal] = React.useState<boolean>(false);
  const [selectedAssetGroup, setSelectedAssetGroup] = React.useState<any>(null);
  const [assetSearch, setAssetSearch] = React.useState<string>('');
  const [assetGroupSearch, setAssetGroupSearch] = React.useState<string>('');
  const [assetGroupDetailsOpen, setAssetGroupDetailsOpen] = React.useState<boolean>(false);
  const [selectedAssetGroupSection, setSelectedAssetGroupSection] = React.useState<AssetSection | null>(null);
  const [reportModalOpen, setReportModalOpen] = React.useState(false);
  const [reportTargetAssetGroup, setReportTargetAssetGroup] = React.useState<any>(null);
  const [historyModalOpen, setHistoryModalOpen] = React.useState(false);
  const [historyTargetAssetGroup, setHistoryTargetAssetGroup] = React.useState<any>(null);
  const [reportHistoryByAssetGroup, setReportHistoryByAssetGroup] = React.useState<Record<string, IAssetGroupReportHistoryItem[]>>({});
  const [historyPaginationByAssetGroup, setHistoryPaginationByAssetGroup] = React.useState<
    Record<string, { page: number; total: number }>
  >({});
  const [historyLoadingByAssetGroup, setHistoryLoadingByAssetGroup] = React.useState<Record<string, boolean>>({});
  
  // Pagination for asset group sections
  const SECTIONS_PAGE_SIZE = 6;
  const HISTORY_PAGE_SIZE = 10;
  const demoCheck = (authStore?.currentUser?.active_role?.role_type === -1);

  const fetchGroups = async (pageNumber: number = 1, keyword = '', orgid?: string) => {
    try {
      setLoading(true);
      const getGroups = demoCheck ? groupServices.getGroupsForDemo : groupServices.getGroups;
      const resp = await getGroups({ page: pageNumber, keyword, orgid });
      if (resp) {
        const { groups, count } = resp?.data || {} as any;
        setGroupList(groups || []);
        setCount(count || 0);
        setPage(pageNumber);
      }
    } catch (error: any) {
      let errMsg = error?.message;
      if (error?.response?.data?.message) {
        errMsg = error?.response?.data?.message;
      }
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAssetSearch = async (searchTerm: string) => {
    setAssetSearch(searchTerm);
    await fetchGroups(1, searchTerm, selectedOrgId);
  };

  const handleClearSearch = async () => {
    setAssetSearch('');
    await fetchGroups(1, '', selectedOrgId);
  };

  const handleAssetGroupSearch = async (searchTerm: string) => {
    setAssetGroupSearch(searchTerm);
    await fetchAssetGroups(1, searchTerm, selectedOrgId);
  };

  const handleClearAssetGroupSearch = async () => {
    setAssetGroupSearch('');
    await fetchAssetGroups(1, '', selectedOrgId);
  };

  const fetchAssetGroups = async (pageNumber: number = 1, keyword = '', orgid?: string) => {
    try {
      setAssetGroupLoading(true);
      const getAssetGroups = demoCheck ? assetGroupService.getAssetGroupsForDemo : assetGroupService.getAssetGroups;
      const resp = await getAssetGroups({ page: pageNumber, limit: SECTIONS_PAGE_SIZE, keyword, orgid });
      if (resp) {
        const { assetGroups, total, count } = resp?.data || {} as any;
        setAssetGroupList(assetGroups || []);
        setAssetGroupCount(total ?? count ?? (assetGroups?.length || 0));
        setAssetGroupPage(pageNumber);
        setSectionsPage(pageNumber);
      }
    } catch (error: any) {
      let errMsg = error?.message;
      if (error?.response?.data?.message) {
        errMsg = error?.response?.data?.message;
      }
      toast.error(errMsg);
    } finally {
      setAssetGroupLoading(false);
    }
  };

  React.useEffect(() => {
    const init = async () => {
      try {
        if (authStore?.isSuperAdmin ||
          (authStore?.isOrgAdmin() && authStore.currentUser.is_default)
        ) {
          const response = await organizationService.getOrganizations();
          const firstOrg = response?.data?.organizations?.[0];
          const orgId = firstOrg?._id as string | undefined;
          setSelectedOrgId(orgId);
          await fetchGroups(1, '', orgId);
          await fetchAssetGroups(1, '', orgId);
        } else {
          await fetchGroups(1);
          await fetchAssetGroups(1);
        }
      } catch (e) {
        await fetchGroups(1);
        await fetchAssetGroups(1);
      }
    };
    init();
  }, []);

  const canManageAssetGroup = authStore.isOrgAdmin();
  const canGenerateReport = authStore.isOrgAdmin() || authStore.isSuperAdmin;

  const loadReportHistory = async (assetGroupId: string, page = 1) => {
    if (!assetGroupId || historyLoadingByAssetGroup[assetGroupId]) {
      return;
    }
    setHistoryLoadingByAssetGroup((prev) => ({ ...prev, [assetGroupId]: true }));
    try {
      const result = await monthlyReportService.getHistory(assetGroupId, page, HISTORY_PAGE_SIZE);
      setReportHistoryByAssetGroup((prev) => ({ ...prev, [assetGroupId]: result.items || [] }));
      setHistoryPaginationByAssetGroup((prev) => ({
        ...prev,
        [assetGroupId]: { page, total: result.total ?? (result.items || []).length },
      }));
    } catch (error: any) {
      let errMsg = error?.message;
      if (error?.response?.data?.detail) {
        errMsg = error?.response?.data?.detail;
      }
      toast.error(errMsg || 'Unable to load report history');
    } finally {
      setHistoryLoadingByAssetGroup((prev) => ({ ...prev, [assetGroupId]: false }));
    }
  };

  const handleReportDownload = async (record: IAssetGroupReportHistoryItem, format: 'xlsx' | 'pptx') => {
    try {
      const response = await monthlyReportService.downloadHistoryReport(record.id, format);
      const contentType = response.headers?.['content-type'] || '';

      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const payload = JSON.parse(text);
        if (payload?.download_url) {
          window.open(payload.download_url, '_blank', 'noopener,noreferrer');
          return;
        }
      }

      const mimeType =
        format === 'pptx'
          ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const defaultName =
        format === 'pptx'
          ? record.pptx_file_name || `monthly_report_${record.id}.pptx`
          : record.file_name || `monthly_report_${record.id}.xlsx`;

      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = defaultName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      let errMsg = error?.message;
      if (error?.response?.data?.detail) {
        errMsg = error?.response?.data?.detail;
      }
      toast.error(errMsg || 'Unable to download report');
    }
  };

  const handleAssetGroupAction = (action: AssetGroupAction, assetGroup: any) => {
    if (action === 'generate-report') {
      if (!canGenerateReport) {
        return;
      }
      setReportTargetAssetGroup(assetGroup);
      setReportModalOpen(true);
      return;
    }
    if (action === 'report-history') {
      if (!canGenerateReport) {
        return;
      }
      setHistoryTargetAssetGroup(assetGroup);
      setHistoryModalOpen(true);
      if (assetGroup?._id) {
        loadReportHistory(assetGroup._id);
      }
      return;
    }
    if (!canManageAssetGroup) return;

    if (action === 'edit') {
          setSelectedAssetGroup(assetGroup);
          setEditAssetGroupModal(true);
    }
    if (action === 'delete') {
      Modal.confirm({
        title: `Delete ${assetGroup.name}?`,
        content: 'This action cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await assetGroupService.deleteAssetGroup(assetGroup._id, selectedOrgId);
            toast.success('Asset group deleted successfully');
            const nextPage = Math.max(1, Math.ceil((assetGroupCount - 1) / SECTIONS_PAGE_SIZE));
            await fetchAssetGroups(Math.min(assetGroupPage, nextPage), assetGroupSearch, selectedOrgId);
          } catch (error: any) {
            let errMsg = error?.message;
            if (error?.response?.data?.message) {
              errMsg = error?.response?.data?.message;
            }
            toast.error(errMsg);
          }
        }
      });
    }
  };

  const formatDateRange = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '-';
    return `${startDate} - ${endDate}`;
  };

  const formatCreatedDate = (createdAt?: number): string => {
    if (!createdAt) return '-';
    return new Date(createdAt).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatReportMonth = (startDate?: string): string => {
    if (!startDate || startDate.length < 6) return '-';
    return `${startDate.slice(0, 4)}-${startDate.slice(4, 6)}`;
  };

  const editGroup = async (group: any) => {
    const regularUser = (
      group?.contractor?.[0]?._id === authStore?.currentUser?.uid ||
      (authStore?.currentUser?.access?.includes(view_assigned_users_sensorboxes_assets)
        ? group?.contractor?.[0]?._id === authStore?.currentUser?.assigned_to
        : false) ||
      authStore?.currentUser?.active_role?.role_type < 2
    );

    if (
      authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets) ||
      authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details)
    ) {
      if (group?.user_access?.includes(authStore?.currentUser?.uid) && !regularUser) {
        return;
      }
      setSelectedGroup(group);
      setModalVisible(true);
    }
  };

  const showConfirmation = () => {
    setModalVisible(false);
    Utility.showModal('confirmmodal');
  };

  const closeConfirmation = () => {
    Utility.closeModal('confirmmodal');
  };

  const deleteGroup = async () => {
    try {
      if (!selectedGroup) return;
      await groupServices.deleteGroup(selectedGroup?._id);
      setModalVisible(false);
      Utility.closeModal('confirmmodal');
      let nextPage = Math.ceil((count - 1) / constants.maxLimit);
      nextPage = nextPage <= 0 ? 1 : nextPage;
      await fetchGroups(nextPage, assetSearch, selectedOrgId);
    } catch (error: any) {
      let errMsg = error?.message;
      if (error?.response?.data?.message) {
        errMsg = error?.response?.data?.message;
      }
      toast.error(errMsg);
    }
  };

  const handleAssetsAction = (action: AssetAction, group: any) => {
    if (action === 'edit') return editGroup(group);
    if (action === 'delete') {
      if (authStore?.isOrgAdmin() || authStore?.currentUser?.access?.includes(delete_sensorboxes_assets)) {
        setSelectedGroup(group);
        return showConfirmation();
      }
    }
  };

  // Convert asset groups to sections for display
  const assetGroupSections: AssetSection[] = assetGroupList.map((group: any) => ({
    key: group._id,
    title: group.name,
    description: group.description || 'Asset Group',
    totalAssetsText: `${group.assets?.length || 0} Assets`,
    totalBoxesText: `${group.sensorboxes?.length || 0} Boxes`,
    collapsed: true,
    assets: (group.assets || []).map((asset: any) => ({
      id: asset._id,
      name: asset.name,
      sensorBoxCount: asset?.sensorboxes?.length || 0
    })),
    sensorboxes: (group.sensorboxes || []).map((box: any) => ({
      id: box._id,
      name: box.sensor_box_name || box.alias,
      place: box.place
    }))
  }));

  const openAssetGroupDetails = (section: AssetSection) => {
    setSelectedAssetGroupSection(section);
    setAssetGroupDetailsOpen(true);
  };

  const historyModalAgId = historyTargetAssetGroup?._id || '';
  const historyModalRows = reportHistoryByAssetGroup[historyModalAgId] || [];
  const historyModalPaging = historyPaginationByAssetGroup[historyModalAgId];
  const historyModalTotal = historyModalPaging?.total ?? 0;

  return (
    <div className="asset-groups-page" style={{ padding: 16 }}>
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Asset Groups</Title>
          <Text>Organize your assets into location based groups for better management</Text>
        </Col>
        <Col>
          {(!demoCheck && 
            <Row justify="space-between" align="middle" gutter={16}>
              <Col>
                  <Input.Search
                    placeholder="Search asset groups by name"
                    value={assetGroupSearch}
                    onChange={(e) => {
                      handleAssetGroupSearch(e.target.value);
                      if (e.target.value === '') {
                        handleClearAssetGroupSearch();
                      }
                    }}
                    allowClear
                    enterButton
                    size="large"
                    style={{ maxWidth: 300 }}
                  />
              </Col>
              <Col>
                <Button 
                  type="primary" 
                  className="asset-groups-create-btn"
                  style={{ height: 40, backgroundColor: '#4F8C92', borderColor: '#4F8C92' }} 
                  icon={<PlusOutlined />} 
                  size="middle" 
                  onClick={() =>{
                    if (authStore.isOrgAdmin()) {
                      setCreateModal(true)
                    }
                  }}
                >Create Group</Button>
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      {assetGroupLoading ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin />
        </div>
      ) : assetGroupSections.length > 0 ? (
        <>
          <Row gutter={[16, 16]}>
            {assetGroupSections.map((section) => (
              <Col key={section.key} xs={24} md={12}>
                <Card
                  hoverable
                  onClick={() => openAssetGroupDetails(section)}
                  style={{
                    borderRadius: 12,
                    border: '1px solid #e1efef',
                    boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
                    cursor: 'pointer',
                    height: 100,
                  }}
                  bodyStyle={{ padding: 16, height: '100%' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0, flex: 1, paddingRight: 12 }}>
                      <Title level={4} style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {section.title}
                      </Title>
                      <Text
                        type="secondary"
                        style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {authStore.isOrgAdmin() ? section.description : 'Asset Group'}
                      </Text>
                    </div>
                    <div style={{ minWidth: 130 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 28px', alignItems: 'center', columnGap: 8, marginBottom: 4 }}>
                        <Text type="secondary" style={{ textAlign: 'right' }}>{section.totalAssetsText}</Text>
                        {canGenerateReport && (
                          <Dropdown
                            overlay={
                              <Menu
                                selectable={false}
                                onClick={({ key, domEvent }) => {
                                  domEvent.stopPropagation();
                                  handleAssetGroupAction(key as AssetGroupAction, assetGroupList.find(g => g._id === section.key));
                                }}
                                items={[
                                  { key: 'generate-report', icon: <FileExcelOutlined />, label: 'Generate Report' },
                                  { key: 'report-history', icon: <FileExcelOutlined />, label: 'Report History' },
                                  ...(canManageAssetGroup
                                    ? [
                                        { type: 'divider' as const },
                                        { key: 'edit', icon: <EditOutlined />, label: 'Edit' },
                                        { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
                                      ]
                                    : []),
                                ]}
                              />
                            }
                            trigger={['click']}
                            placement="bottomRight"
                          >
                            <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                          </Dropdown>
                        )}
                        {!canGenerateReport && <div />}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 28px', alignItems: 'center', columnGap: 8 }}>
                        <Text type="secondary" style={{ textAlign: 'right' }}>{section.totalBoxesText}</Text>
                        <div />
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {(authStore.isOrgAdmin() && Math.ceil(assetGroupCount / SECTIONS_PAGE_SIZE) > 1) && (
            <div style={{ margin: '8px 0 24px', textAlign: 'center', display:"flex", justifyContent:"end" }}>
              <Pagination
                hideNavigation
                activePage={sectionsPage}
                itemsCountPerPage={SECTIONS_PAGE_SIZE}
                totalItemsCount={assetGroupCount}
                itemClass={'item-class'}
                activeClass={'active-class'}
                pageRangeDisplayed={5}
                onChange={(p: number) => fetchAssetGroups(p, assetGroupSearch, selectedOrgId)}
              />
            </div>
          )}
        </>
      ) : (
        <Empty description={assetGroupSearch ? "No asset groups found matching your search" : "No asset groups found"} />
      )}

      <Modal
        visible={assetGroupDetailsOpen}
        onCancel={() => setAssetGroupDetailsOpen(false)}
        footer={null}
        width={960}
        title={<span style={{ color: '#4F8C92', fontWeight: 700 }}>{selectedAssetGroupSection?.title || 'Asset Group'}</span>}
        bodyStyle={{ background: '#f7fbfb' }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col><Text type="secondary">{selectedAssetGroupSection?.description || 'Asset Group'}</Text></Col>
          <Col>
            <Text strong style={{ color: '#4F8C92' }}>
              {selectedAssetGroupSection?.totalAssetsText || '0 Assets'} | {selectedAssetGroupSection?.totalBoxesText || '0 Boxes'}
            </Text>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card
              title={<span style={{ color: '#4F8C92', fontWeight: 600 }}>Assets</span>}
              style={{ borderRadius: 12, border: '1px solid #d9ecec' }}
              bodyStyle={{ maxHeight: 420, overflowY: 'auto' }}
            >
              {selectedAssetGroupSection?.assets?.length ? (
                selectedAssetGroupSection.assets.map((item) => (
                  <div key={item.id} style={{ marginBottom: 12 }}>
                    <AssetGroupsAssetCard item={item} />
                  </div>
                ))
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No assets" />
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card
              title={<span style={{ color: '#4F8C92', fontWeight: 600 }}>Sensor Boxes</span>}
              style={{ borderRadius: 12, border: '1px solid #d9ecec' }}
              bodyStyle={{ maxHeight: 420, overflowY: 'auto' }}
            >
              {selectedAssetGroupSection?.sensorboxes?.length ? (
                selectedAssetGroupSection.sensorboxes.map((item) => (
                  <div key={item.id} style={{ marginBottom: 12 }}>
                    <BoxCard item={item} />
                  </div>
                ))
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sensor boxes" />
              )}
            </Card>
          </Col>
        </Row>
      </Modal>

      {/* Assets from GroupList */}
      <div style={{ marginTop: 24 }}>
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #f0f0f0',
            padding: 16,
            boxShadow: '0 12px 28px rgba(0,0,0,0.18)'
          }}
        >
          <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
            <Col>
              <Title level={4} style={{ marginBottom: 0 }}>Assets</Title>
              <div><Text type="secondary">All assets</Text></div>
            </Col>
            <Col>
              {(!demoCheck && 
                <Input.Search
                  placeholder="Search assets by name"
                  value={assetSearch}
                  onChange={(e) => {
                    handleAssetSearch(e.target.value);
                    if (e.target.value === '') {
                      handleClearSearch();
                    }
                  }}
                  allowClear
                  enterButton
                  size="large"
                  style={{ maxWidth: 300 }}
                />
              )}
            </Col>
          </Row>
          <div style={{ marginTop: 16 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <Spin />
              </div>
            ) : groupList?.length ? (
              <Row gutter={[16, 16]}>
                {groupList.map((group: any, index: number) => (
                  <Col key={group?._id} xs={24} sm={12} md={8} lg={6}>
                    <AssetCard
                      item={{ id: group?._id, name: group?.name, sensorBoxCount: group?.sensorboxes?.length || 0 }}
                      payload={group}
                      index={index}
                      onAction={handleAssetsAction}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty description={assetSearch ? "No assets found matching your search" : "No assets found"} />
            )}
          </div>
          {(authStore.isOrgAdmin() && Math.ceil(count / constants.maxLimit) > 1) && (
            <div style={{ marginTop: 16, textAlign: 'center' , display:"flex", justifyContent:"end" }}>
              <Pagination
                hideNavigation
                activePage={page}
                itemsCountPerPage={constants.maxLimit}
                totalItemsCount={count}
                itemClass={'item-class'}
                activeClass={'active-class'}
                pageRangeDisplayed={5}
                onChange={(pageNumber: number) => fetchGroups(pageNumber, assetSearch, selectedOrgId)}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Modals to edit/delete assets */}
      <CreateGroupModal
        show={modalVisible}
        onClose={() => setModalVisible(false)}
        group={selectedGroup}
        organisationId={selectedOrgId}
        onSave={() => fetchGroups(page, assetSearch, selectedOrgId)}
        onDelete={showConfirmation}
        editMode
      />
      {/* Create Asset Group */}
      <CreateAssetGroupModal
        show={createModal}
        onClose={() => setCreateModal(false)}
        onCreated={() => fetchAssetGroups(1, assetGroupSearch, selectedOrgId)}
        organisationId={selectedOrgId as any}
      />
      {/* Edit Asset Group */}
      <EditAssetGroupModal
        show={editAssetGroupModal}
        onClose={() => setEditAssetGroupModal(false)}
        onUpdated={() => fetchAssetGroups(assetGroupPage, assetGroupSearch, selectedOrgId)}
        organisationId={selectedOrgId as any}
        assetGroup={selectedAssetGroup}
      />
      <Confirmmodal
        title="Delete Asset Group"
        message={`Do you want to delete asset group ${selectedGroup?.name} ?`}
        closeConfirmModal={closeConfirmation}
        delete={deleteGroup}
        isLoading={loading}
      />
      <GenerateMonthlyReportModal
        visible={reportModalOpen}
        assetGroupId={reportTargetAssetGroup?._id || ''}
        assetGroupName={reportTargetAssetGroup?.name || ''}
        onClose={() => setReportModalOpen(false)}
        onSubmitted={() => {
          if (reportTargetAssetGroup?._id) {
            loadReportHistory(reportTargetAssetGroup._id, 1);
          }
          toast.success('Report generation started and it will be sent to your email once ready to download.');
        }}
      />
      <Modal
        visible={historyModalOpen}
        title={<span style={{ color: '#22515a', fontWeight: 600 }}>{`Report History - ${historyTargetAssetGroup?.name || ''}`}</span>}
        onCancel={() => setHistoryModalOpen(false)}
        footer={null}
        width={760}
        bodyStyle={{ background: '#f7fbfb' }}
      >
        <Row align="middle" justify="space-between" style={{ marginBottom: 8 }}>
          <Col><Text strong style={{ color: '#22515a' }}>Report History</Text></Col>
          <Col>
            <Button
              type="primary"
              size="small"
              style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92' }}
              onClick={() => historyModalAgId && loadReportHistory(historyModalAgId, 1)}
            >
              Refresh
            </Button>
          </Col>
        </Row>
        {historyModalAgId && historyLoadingByAssetGroup[historyModalAgId] ? (
          <Spin size="small" />
        ) : historyModalTotal > 0 ? (
          <Table
            className="style-table"
            rowKey="id"
            size="small"
            dataSource={historyModalRows}
            pagination={{
              current: historyModalPaging?.page ?? 1,
              pageSize: HISTORY_PAGE_SIZE,
              total: historyModalTotal,
              showSizeChanger: false,
              hideOnSinglePage: true,
              onChange: (p) => loadReportHistory(historyModalAgId, p),
            }}
            columns={[
              {
                title: 'Created Date',
                key: 'created_date',
                render: (_, record: IAssetGroupReportHistoryItem) => (
                  <span>{formatCreatedDate(record.created_at)}</span>
                ),
              },
              {
                title: 'Report Month',
                key: 'report_month',
                render: (_, record: IAssetGroupReportHistoryItem) => (
                  <span>{formatReportMonth(record.start_date)}</span>
                ),
              },
              {
                title: 'Status',
                key: 'status',
                render: (_, record: IAssetGroupReportHistoryItem) => (
                  <Tag color={record.status === 'delivered' ? 'green' : record.status === 'succeeded' ? 'blue' : record.status === 'failed' ? 'red' : 'blue'}>
                    {record.status}
                  </Tag>
                ),
              },
              {
                title: 'Action',
                key: 'action',
                align: 'center',
                render: (_, record: IAssetGroupReportHistoryItem) => (
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <Button
                      type="primary"
                      icon={<FileExcelOutlined />}
                      title="Download XLSX"
                      style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92' }}
                      disabled={!['succeeded', 'delivered'].includes(record.status) || !record.file_name}
                      onClick={() => handleReportDownload(record, 'xlsx')}
                    />
                    <Button
                      type="primary"
                      icon={<FilePptOutlined />}
                      title="Download PPTX"
                      style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92' }}
                      disabled={
                        !['succeeded', 'delivered'].includes(record.status) || !record.pptx_file_name
                      }
                      onClick={() => handleReportDownload(record, 'pptx')}
                    />
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <Text type="secondary">No reports generated yet.</Text>
        )}
      </Modal>
      
    </div>
  );
};

export default AssetGroups;